/**
 * LMD calculation engine — pure functions, no I/O. Every rule that varies between
 * institutions comes from `GradingRules` (tenant settings), never from literals.
 *
 * Rounding: every level (course, UE, semester) is shown rounded half-up to `decimals`.
 * `rounding: 'each_level'` carries those rounded values to the next level (what you see is
 * what is used); `'final'` carries full precision and rounds only what is shown and compared.
 */
import { roundHalfUp } from '../format';
import type { TenantSettings } from '../settings';
import type { Regime } from './structure';

export const ASSESSMENT_KINDS = ['DS', 'TP', 'EXAM', 'RATTRAPAGE'] as const;
export type AssessmentKind = (typeof ASSESSMENT_KINDS)[number];

export const DECISIONS = ['ADMIS', 'ADMIS_COMPENSATION', 'RATTRAPAGE', 'AJOURNE'] as const;
export type Decision = (typeof DECISIONS)[number];

export type SessionKind = 'principale' | 'rattrapage';
export type GradingRules = TenantSettings['grading'];

export interface Mark {
  value: number | null;
  absent: boolean;
}
export type Marks = Partial<Record<AssessmentKind, Mark>>;

/** Assessments that exist for a régime (the grade-sheet columns). */
export function expectedAssessments(regime: Regime, rules: GradingRules): AssessmentKind[] {
  const { cc, exam } = rules.regimes[regime];
  const kinds: AssessmentKind[] = [];
  if (cc > 0) {
    if (rules.ccComponents.DS > 0) kinds.push('DS');
    if (rules.ccComponents.TP > 0) kinds.push('TP');
  }
  if (exam > 0) kinds.push('EXAM');
  return kinds;
}

/**
 * Percent weight of each assessment (0 when not on the sheet); the CC share is redistributed
 * over the CC kinds present. RATTRAPAGE never has its own weight: it substitutes the exam.
 */
export function weightsFor(
  regime: Regime,
  expected: readonly AssessmentKind[],
  rules: GradingRules,
): Record<AssessmentKind, number> {
  const { cc, exam } = rules.regimes[regime];
  const ccKinds = expected.filter((k): k is 'DS' | 'TP' => k === 'DS' || k === 'TP');
  const share = ccKinds.reduce((a, k) => a + rules.ccComponents[k], 0);
  const out: Record<AssessmentKind, number> = { DS: 0, TP: 0, EXAM: 0, RATTRAPAGE: 0 };
  for (const k of ccKinds) out[k] = share > 0 ? (cc * rules.ccComponents[k]) / share : cc / ccKinds.length;
  if (expected.includes('EXAM')) out.EXAM = exam;
  return out;
}

export interface CourseInput {
  regime: Regime;
  /** Columns of the grade sheet (DS / TP / EXAM). RATTRAPAGE is read from `marks` in the retake session. */
  expected: readonly AssessmentKind[];
  marks: Marks;
  /** Set by the attendance module when unjustified absences exceed the threshold (Phase 5). */
  eliminatedByAbsence?: boolean;
}

export interface CourseResult {
  average: number | null;
  /** Unrounded average, carried to the UE when `rounding` is 'final'. */
  exact: number | null;
  complete: boolean;
  missing: AssessmentKind[];
  /** The retake mark was used for the exam component. */
  usedRetake: boolean;
  eliminated: boolean;
}

function markValue(kind: AssessmentKind, mark: Mark | undefined, rules: GradingRules): number | null {
  if (!mark) return null;
  if (mark.absent)
    return (kind === 'EXAM' || kind === 'RATTRAPAGE') && rules.absentAtExam === 'missing' ? null : 0;
  return mark.value;
}

export function computeCourse(
  input: CourseInput,
  rules: GradingRules,
  session: SessionKind = 'principale',
): CourseResult {
  const weights = weightsFor(input.regime, input.expected, rules);
  const missing: AssessmentKind[] = [];
  let usedRetake = false;
  let sum = 0;
  let total = 0;
  for (const kind of input.expected) {
    const w = weights[kind];
    let v = markValue(kind, input.marks[kind], rules);
    if (kind === 'EXAM' && session === 'rattrapage') {
      const r = markValue('RATTRAPAGE', input.marks.RATTRAPAGE, rules);
      if (r !== null) {
        v = rules.retake === 'replace' || v === null ? r : Math.max(v, r);
        usedRetake = true;
      } else if (input.marks.RATTRAPAGE) {
        // A retake was scheduled for this course (empty mark row): the result waits for it.
        missing.push('RATTRAPAGE');
      }
    }
    if (v === null) {
      missing.push(kind);
      continue;
    }
    sum += w * v;
    total += w;
  }
  const complete = missing.length === 0 && input.expected.length > 0;
  return {
    average: complete && total > 0 ? roundHalfUp(sum / total, rules.decimals) : null,
    exact: complete && total > 0 ? sum / total : null,
    complete,
    missing,
    usedRetake,
    eliminated: input.eliminatedByAbsence === true,
  };
}

export interface UnitCourse {
  coefficient: number;
  result: CourseResult;
}
export interface UnitInput {
  credits: number;
  /** UE override; falls back to the tenant eliminatory grade. */
  eliminatoryGrade: number | null;
  /** The student's courses (for an optional UE: the chosen one only). */
  courses: readonly UnitCourse[];
}
export interface UnitResult {
  average: number | null;
  /** Unrounded average, carried to the semester when `rounding` is 'final'. */
  exact: number | null;
  complete: boolean;
  acquired: boolean;
  /** Average below the eliminatory grade, or a course eliminated for absences: blocks compensation. */
  eliminatory: boolean;
  credits: number;
}

/** Value carried to the next level: the rounded average, or full precision when rounding is 'final'. */
const carried = (r: { average: number | null; exact: number | null }, rules: GradingRules) =>
  rules.rounding === 'final' ? r.exact : r.average;

/** Weighted mean; falls back to the plain mean when every weight is 0. */
function weightedMean(items: readonly { weight: number; value: number }[]): number {
  const total = items.reduce((a, i) => a + i.weight, 0);
  return total > 0
    ? items.reduce((a, i) => a + i.weight * i.value, 0) / total
    : items.reduce((a, i) => a + i.value, 0) / items.length;
}

export function computeUnit(unit: UnitInput, rules: GradingRules): UnitResult {
  const items: { weight: number; value: number }[] = [];
  for (const c of unit.courses) {
    const value = carried(c.result, rules);
    if (value === null) break;
    items.push({ weight: c.coefficient, value });
  }
  if (items.length === 0 || items.length < unit.courses.length) {
    return {
      average: null,
      exact: null,
      complete: false,
      acquired: false,
      eliminatory: false,
      credits: unit.credits,
    };
  }
  const exact = weightedMean(items);
  const average = roundHalfUp(exact, rules.decimals);
  const threshold = unit.eliminatoryGrade ?? rules.eliminatoryUeGrade;
  const eliminatedCourse = unit.courses.some((c) => c.result.eliminated);
  const eliminatory = eliminatedCourse || (threshold !== null && average < threshold);
  return {
    average,
    exact,
    complete: true,
    acquired: average >= rules.passMark && !eliminatedCourse,
    eliminatory,
    credits: unit.credits,
  };
}

export interface SemesterUnit {
  id: string;
  credits: number;
  coefficient: number;
  result: UnitResult;
}
export type MentionKey = GradingRules['mentions'][number]['key'];

export interface SemesterResult {
  complete: boolean;
  average: number | null;
  creditsAcquired: number;
  creditsTotal: number;
  validated: boolean;
  byCompensation: boolean;
  /** UEs not acquired on their own but validated through compensation. */
  compensatedUnits: string[];
  /** Why compensation did not apply (null when validated or incomplete). */
  compensationBlockedBy: 'eliminatory' | 'average' | 'disabled' | null;
  decision: Decision | null;
  mention: MentionKey | null;
}

export function mentionFor(average: number, rules: GradingRules): MentionKey | null {
  const sorted = [...rules.mentions].sort((a, b) => b.min - a.min);
  return sorted.find((m) => average >= m.min)?.key ?? null;
}

export function computeSemester(
  units: readonly SemesterUnit[],
  rules: GradingRules,
  session: SessionKind = 'principale',
): SemesterResult {
  const creditsTotal = units.reduce((a, u) => a + u.credits, 0);
  const acquiredCredits = units.filter((u) => u.result.acquired).reduce((a, u) => a + u.credits, 0);
  const items: { weight: number; value: number }[] = [];
  for (const u of units) {
    const value = carried(u.result, rules);
    if (value === null) break;
    items.push({ weight: rules.semesterAveraging === 'credits' ? u.credits : u.coefficient, value });
  }
  if (items.length === 0 || items.length < units.length) {
    return {
      creditsTotal,
      compensatedUnits: [],
      complete: false,
      average: null,
      creditsAcquired: acquiredCredits,
      validated: false,
      byCompensation: false,
      compensationBlockedBy: null,
      decision: null,
      mention: null,
    };
  }
  const average = roundHalfUp(weightedMean(items), rules.decimals);
  const allAcquired = units.every((u) => u.result.acquired);
  const anyEliminatory = units.some((u) => u.result.eliminatory);
  const { enabled, minAverage } = rules.compensation;
  const byCompensation = !allAcquired && enabled && average >= minAverage && !anyEliminatory;
  const validated = allAcquired || byCompensation;
  const compensationBlockedBy = validated
    ? null
    : !enabled
      ? 'disabled'
      : anyEliminatory
        ? 'eliminatory'
        : 'average';
  const decision: Decision = allAcquired
    ? 'ADMIS'
    : byCompensation
      ? 'ADMIS_COMPENSATION'
      : session === 'principale'
        ? 'RATTRAPAGE'
        : 'AJOURNE';
  return {
    creditsTotal,
    complete: true,
    average,
    creditsAcquired: validated ? creditsTotal : acquiredCredits,
    validated,
    byCompensation,
    compensatedUnits: byCompensation ? units.filter((u) => !u.result.acquired).map((u) => u.id) : [],
    compensationBlockedBy,
    decision,
    mention: validated ? mentionFor(average, rules) : null,
  };
}
